'use client';

import dayjs from 'dayjs';
import { ComponentProps, CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { DateRange, DayButton, DayPicker, type DayPickerProps, getDefaultClassNames } from 'react-day-picker';
import { da, enGB } from 'react-day-picker/locale';
import { cn } from '../../lib/utils/classnameVariants';
import { Button } from './button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import './shadcn.css';

// (Header component reverted to simpler Radix Select based layout below)

type CalendarProps = DayPickerProps & {
  buttonVariant?: ComponentProps<typeof Button>['variant'];
  interval?: { from: Date; to: Date };
  /** Selection mode: 'single', 'range', or 'both' (first click = single, second click = range) */
  selectionMode?: 'single' | 'range' | 'both';
  singleSelected?: Date;
  rangeSelected?: DateRange | undefined;
  onSingleSelect?: (date: Date | undefined) => void;
  onRangeSelect?: (range: DateRange | undefined) => void;
  effectiveLocale?: 'da' | 'en';
  /** Portal container for month/year Select dropdowns. Pass the dialog element when inside a Radix Dialog to keep dropdowns within the FocusScope. */
  portalContainer?: HTMLElement | null;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  month,
  defaultMonth,
  onMonthChange,
  formatters,
  components,
  interval,
  selectionMode = 'single',
  singleSelected,
  rangeSelected,
  onSingleSelect,
  onRangeSelect,
  effectiveLocale = 'da',
  portalContainer,
  ...props
}: CalendarProps) {
  const defaultClassNames = getDefaultClassNames();

  const initialMonth = month || defaultMonth || new Date();
  const [currentMonth, setCurrentMonth] = useState<Date>(initialMonth);
  // In 'both' mode we internally use range selection and treat a partial range (only from) as a single selection
  const effectiveMode: 'single' | 'range' = selectionMode === 'single' ? 'single' : 'range';

  useEffect(() => {
    if (month && month.getTime() !== currentMonth.getTime()) setCurrentMonth(month);
  }, [month, currentMonth]);

  // Auto-navigate to a valid month when interval changes
  useEffect(() => {
    if (!interval) return;

    const currentYear = currentMonth.getFullYear();
    const currentMonthIndex = currentMonth.getMonth();
    const fromYear = interval.from.getFullYear();
    const toYear = interval.to.getFullYear();

    // Check if current month is within interval
    let isValidMonth = false;

    if (currentYear >= fromYear && currentYear <= toYear) {
      if (currentYear === fromYear && currentYear === toYear) {
        // Same year - check if month is between from and to
        isValidMonth = currentMonthIndex >= interval.from.getMonth() && currentMonthIndex <= interval.to.getMonth();
      } else if (currentYear === fromYear) {
        // First year - check if month is >= from month
        isValidMonth = currentMonthIndex >= interval.from.getMonth();
      } else if (currentYear === toYear) {
        // Last year - check if month is <= to month
        isValidMonth = currentMonthIndex <= interval.to.getMonth();
      } else {
        // Middle year - all months are valid
        isValidMonth = true;
      }
    }

    // If current month is not valid, navigate to the start of the interval
    if (!isValidMonth) {
      const validMonth = new Date(interval.from.getFullYear(), interval.from.getMonth(), 1);
      setCurrentMonth(validMonth);
      onMonthChange?.(validMonth);
    }
  }, [interval, currentMonth, onMonthChange]);

  const goTo = (date: Date) => {
    setCurrentMonth(date);
    onMonthChange?.(date);
  };

  const years = useMemo(() => {
    if (!interval) {
      const nowYear = new Date().getFullYear();
      const lower = nowYear - 120; // 100 years back from today
      const arr: number[] = [];
      for (let y = nowYear; y >= lower; y--) arr.push(y); // descending order (recent first)
      return arr;
    }

    // Only show years that contain dates within the interval
    const fromYear = interval.from.getFullYear();
    const toYear = interval.to.getFullYear();
    const arr: number[] = [];
    for (let y = toYear; y >= fromYear; y--) arr.push(y); // descending order (recent first)
    return arr;
  }, [interval]);

  const monthLabels = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        dayjs(new Date(2020, i, 1))
          .locale(effectiveLocale === 'da' ? 'da' : 'en')
          .format('MMMM'),
      ),
    [effectiveLocale],
  );

  // Get available months for the current year based on interval
  const availableMonths = useMemo(() => {
    if (!interval) {
      return Array.from({ length: 12 }, (_, i) => i);
    }

    const currentYear = currentMonth.getFullYear();
    const fromYear = interval.from.getFullYear();
    const toYear = interval.to.getFullYear();

    // If current year is outside interval, return empty array
    if (currentYear < fromYear || currentYear > toYear) {
      return [];
    }

    const months: number[] = [];

    if (currentYear === fromYear && currentYear === toYear) {
      // Same year - only include months between from and to
      const fromMonth = interval.from.getMonth();
      const toMonth = interval.to.getMonth();
      for (let m = fromMonth; m <= toMonth; m++) {
        months.push(m);
      }
    } else if (currentYear === fromYear) {
      // First year - include months from interval start to end of year
      const fromMonth = interval.from.getMonth();
      for (let m = fromMonth; m < 12; m++) {
        months.push(m);
      }
    } else if (currentYear === toYear) {
      // Last year - include months from start of year to interval end
      const toMonth = interval.to.getMonth();
      for (let m = 0; m <= toMonth; m++) {
        months.push(m);
      }
    } else {
      // Middle year - include all months
      for (let m = 0; m < 12; m++) {
        months.push(m);
      }
    }

    return months;
  }, [interval, currentMonth]);

  const modifiersClassNames = {
    today: 'rdp-day_today',
    outside: 'rdp-day_outside',
  } as const;

  const mergedClassNames = {
    root: cn('w-fit', defaultClassNames.root),
    months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
    month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
    nav: 'hidden',
    table: 'w-full border-collapse',
    weekdays: cn('flex', defaultClassNames.weekdays),
    weekday: cn(
      'text-muted-foreground flex-1 select-none rounded-md text-[0.8rem] font-normal',
      defaultClassNames.weekday,
    ),
    week: cn('mt-2 flex w-full', defaultClassNames.week),
    week_number_header: cn('w-[--cell-size] select-none', defaultClassNames.week_number_header),
    week_number: cn('text-muted-foreground select-none text-[0.8rem]', defaultClassNames.week_number),
    day: cn(
      'relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
      defaultClassNames.day,
    ),
    range_start: cn('bg-accent rounded-l-md', defaultClassNames.range_start),
    range_middle: cn('rounded-none', defaultClassNames.range_middle),
    range_end: cn('bg-accent rounded-r-md', defaultClassNames.range_end),
    today: cn('rounded-md data-[selected=true]:rounded-none', defaultClassNames.today),
    outside: cn('text-muted-foreground aria-selected:text-muted-foreground', defaultClassNames.outside),
    disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
    hidden: cn('invisible', defaultClassNames.hidden),
    caption_label: 'sr-only',
    ...classNames,
  } as const;

  const mergedComponents = {
    DayButton: CalendarDayButton,
    WeekNumber: ({ children, ...rest }: ComponentProps<'td'>) => (
      <td {...rest}>
        <div className="flex size-[--cell-size] items-center justify-center text-center">{children}</div>
      </td>
    ),
    ...components,
  } as const;

  const dayPickerCommonProps = {
    ...props,
    weekStartsOn: 1 as const,
    month: currentMonth,
    onMonthChange: (m: Date) => goTo(m),
    showOutsideDays,
    captionLayout: 'label' as const,
    className: 'w-fit',
    modifiersClassNames,
    classNames: mergedClassNames,
    components: mergedComponents,
    formatters: { ...formatters },
    locale: effectiveLocale === 'da' ? da : enGB,
  };

  const renderBody = () => {
    if (effectiveMode === 'single') {
      return (
        <DayPicker
          {...dayPickerCommonProps}
          mode="single"
          selected={singleSelected}
          onSelect={(selected: Date | undefined) => {
            onSingleSelect?.(selected);
          }}
        />
      );
    }

    const normalizedRange =
      rangeSelected && rangeSelected.from ? { from: rangeSelected.from, to: rangeSelected.to } : undefined;

    const handleRangeSelect = (selected: DateRange | undefined) => {
      if (!selected) {
        onRangeSelect?.(undefined);
        if (selectionMode !== 'both') onSingleSelect?.(undefined);
        return;
      }
      if (selectionMode === 'both') {
        const { from, to } = selected;
        if (from && !to) {
          onRangeSelect?.({ from, to: undefined });
          return;
        }
        if (from && to && dayjs(from).isSame(to, 'day')) {
          onRangeSelect?.({ from, to });
          return;
        }
        if (from && to) {
          onRangeSelect?.(selected);
          return;
        }
        return;
      }
      onRangeSelect?.(selected);
    };

    return <DayPicker {...dayPickerCommonProps} mode="range" selected={normalizedRange} onSelect={handleRangeSelect} />;
  };

  return (
    <div className={cn('bg-background p-3', className)} style={{ ['--cell-size']: '2rem' } as CSSProperties}>
      <div className="mb-3 flex items-center gap-2">
        <Select
          value={String(currentMonth.getMonth())}
          onValueChange={v => goTo(new Date(currentMonth.getFullYear(), Number(v), 1))}
        >
          <SelectTrigger
            size="sm"
            className="min-w-0 flex-1 capitalize"
            aria-label="Select month"
            data-calendar-select-trigger
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent
            align="start"
            className="max-h-[min(24rem,var(--radix-select-content-available-height))]"
            data-calendar-select-content
            container={portalContainer}
          >
            {availableMonths.map(monthIndex => (
              <SelectItem key={monthIndex} value={String(monthIndex)} className="py-[6px] capitalize">
                {monthLabels[monthIndex]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={String(currentMonth.getFullYear())}
          onValueChange={v => goTo(new Date(Number(v), currentMonth.getMonth(), 1))}
        >
          <SelectTrigger size="sm" className="min-w-0 flex-1" aria-label="Select year" data-calendar-select-trigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent
            align="start"
            className="max-h-[min(24rem,var(--radix-select-content-available-height))]"
            data-calendar-select-content
            container={portalContainer}
          >
            {years.map(y => (
              <SelectItem key={y} value={String(y)} className="py-[6px]">
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {renderBody()}
    </div>
  );
}

function CalendarDayButton({ className, day, modifiers, ...props }: ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);
  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'leading-none flex aspect-square h-auto w-full min-w-[--cell-size] flex-col gap-1 font-normal data-[range-end=true]:rounded-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-end=true]:bg-primary data-[range-middle=true]:bg-accent data-[range-start=true]:bg-primary data-[selected-single=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:text-accent-foreground data-[range-start=true]:text-primary-foreground data-[selected-single=true]:text-primary-foreground group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-ring/50 [&>span]:text-xs [&>span]:opacity-70',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
